/**
 * Audio recording + playback for the web bridge.
 *
 * Storage backend: Blobs in the IndexedDB `binary` store, scoped per
 * module — same logical layout as Android's v2_storage/<moduleId>/<file>.
 *
 * Recording:
 *   - getUserMedia({audio: true}) + MediaRecorder
 *   - chunks collected via dataavailable; final blob assembled on stop
 *   - blob saved to IDB; a blob URL is cached so getAudioFileUrl can
 *     return synchronously (matches Android contract)
 *
 * Playback:
 *   - one HTMLAudioElement per module (matches Android's single-player
 *     model). Reused across play/pause/resume/stop. Recreated on play
 *     of a new file.
 *
 * Both bridge contracts are kept synchronous from the module's
 * perspective even though IDB I/O is async — modules see Android-style
 * methods that return immediately.
 */

import { storage } from './storage.js';

interface RecordingState {
  recorder: MediaRecorder;
  stream: MediaStream;
  fileName: string;
  chunks: Blob[];
}

interface PlaybackState {
  audio: HTMLAudioElement;
  fileName: string;
  /** Blob URL currently set on the element; revoked when replaced. */
  url: string;
}

const recordings = new Map<string, RecordingState>();
const playbacks = new Map<string, PlaybackState>();
const blobUrlCache = new Map<string, string>(); // key: moduleId::fileName

function cacheKey(moduleId: string, fileName: string): string {
  return `${moduleId}::${fileName}`;
}

// ─── Recording ───────────────────────────────────────────────────────────────

/**
 * Starts recording. Returns 'SUCCESS' optimistically; the actual stream
 * setup is async. Failures surface in the console (e.g. permission denied).
 * Modules should call requestMicrophonePermission first.
 */
export function startRecording(moduleId: string, fileName: string): string {
  if (recordings.has(moduleId)) return 'ERROR: Already recording';
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'ERROR: Audio recording not supported';
  }
  if (typeof MediaRecorder === 'undefined') {
    return 'ERROR: MediaRecorder not supported';
  }
  if (!fileName) return 'ERROR: fileName is required';

  void (async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Re-check after the async wait — module may have called stopRecording
      // already (unlikely) or we may have raced another start.
      if (recordings.has(moduleId)) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.addEventListener('dataavailable', (e) => {
        const ev = e as BlobEvent;
        if (ev.data && ev.data.size > 0) chunks.push(ev.data);
      });
      recorder.start(500); // emit a chunk every 500ms so stop() finalizes quickly
      recordings.set(moduleId, { recorder, stream, fileName, chunks });
    } catch (e) {
      console.error('[audio] startRecording failed', e);
    }
  })();
  return 'SUCCESS';
}

/**
 * Stops recording. Returns the filename synchronously; the blob assembly
 * + IDB save happens in the background. Modules that want to play the
 * file immediately after should wait briefly or listen for an event.
 */
export function stopRecording(moduleId: string): string {
  const state = recordings.get(moduleId);
  if (!state) return '';
  const { fileName } = state;
  // Detach state immediately so a subsequent startRecording can proceed.
  recordings.delete(moduleId);

  // Save in background.
  void (async () => {
    try {
      await new Promise<void>((resolve) => {
        state.recorder.addEventListener('stop', () => resolve(), { once: true });
        if (state.recorder.state !== 'inactive') state.recorder.stop();
        else resolve();
      });
      state.stream.getTracks().forEach((t) => t.stop());
      const mime = state.recorder.mimeType || 'audio/webm';
      const blob = new Blob(state.chunks, { type: mime });
      await storage.putBinary({ moduleId, key: fileName }, blob);
      // Pre-cache the blob URL so getAudioFileUrl works sync.
      const key = cacheKey(moduleId, fileName);
      const existing = blobUrlCache.get(key);
      if (existing) URL.revokeObjectURL(existing);
      blobUrlCache.set(key, URL.createObjectURL(blob));
    } catch (e) {
      console.error('[audio] stopRecording save failed', e);
    }
  })();

  return fileName;
}

// ─── Playback ────────────────────────────────────────────────────────────────

/**
 * Plays a recorded audio file. Async-loads the blob from IDB if it's
 * not already cached; the play call itself is awaited internally so the
 * module sees a sync method.
 */
export function playAudio(moduleId: string, fileName: string): void {
  if (!fileName) return;
  void (async () => {
    try {
      let url = blobUrlCache.get(cacheKey(moduleId, fileName));
      if (!url) {
        const blob = await storage.getBinary({ moduleId, key: fileName });
        if (!blob) {
          console.warn(`[audio] file not found: ${fileName}`);
          return;
        }
        url = URL.createObjectURL(blob);
        blobUrlCache.set(cacheKey(moduleId, fileName), url);
      }

      let state = playbacks.get(moduleId);
      // If the module is playing a different file, swap.
      if (state && state.fileName !== fileName) {
        try { state.audio.pause(); } catch {}
        state.audio.src = url;
        state.fileName = fileName;
        state.url = url;
      } else if (!state) {
        const audio = new Audio(url);
        state = { audio, fileName, url };
        playbacks.set(moduleId, state);
      }
      try { await state.audio.play(); }
      catch (e) { console.warn('[audio] play failed', e); }
    } catch (e) {
      console.error('[audio] playAudio failed', e);
    }
  })();
}

export function pauseAudio(moduleId: string): void {
  const state = playbacks.get(moduleId);
  if (!state) return;
  try { state.audio.pause(); } catch {}
}

export function resumeAudio(moduleId: string): void {
  const state = playbacks.get(moduleId);
  if (!state) return;
  try { void state.audio.play(); } catch {}
}

export function stopAudio(moduleId: string): void {
  const state = playbacks.get(moduleId);
  if (!state) return;
  try {
    state.audio.pause();
    state.audio.currentTime = 0;
  } catch {}
}

/**
 * Returns a blob URL usable directly in <audio src="...">. Synchronous —
 * if the URL isn't cached we kick off an async load and return "" for
 * now. Subsequent calls (or after a startRecording → stopRecording cycle)
 * return the URL. Mirrors Android's getAudioFileUrl contract.
 */
export function getAudioFileUrl(moduleId: string, fileName: string): string {
  const key = cacheKey(moduleId, fileName);
  const cached = blobUrlCache.get(key);
  if (cached) return cached;
  // Background load — populates the cache for next call.
  void (async () => {
    try {
      const blob = await storage.getBinary({ moduleId, key: fileName });
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      blobUrlCache.set(key, url);
    } catch (e) {
      console.warn('[audio] getAudioFileUrl background load failed', e);
    }
  })();
  return '';
}

/** Removes a recorded file from storage and revokes its cached URL. */
export async function deleteAudio(moduleId: string, fileName: string): Promise<void> {
  const key = cacheKey(moduleId, fileName);
  const url = blobUrlCache.get(key);
  if (url) {
    URL.revokeObjectURL(url);
    blobUrlCache.delete(key);
  }
  await storage.deleteBinary({ moduleId, key: fileName });
}
